import React from "react";
import { Box, Stack, Typography, Chip, Divider } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MemoryIcon from "@mui/icons-material/Memory";

const fmtDateTime = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  return x.toLocaleString();
};

const ACTION_LABEL = {
  workflow_change: "Workflow change",
  schedule: "Schedule",
  outreach: "Outreach",
  submit: "Submit to Availity",
  status_change: "Hability sync",
};

const ACTION_COLOR = {
  workflow_change: "#1976d2",
  schedule: "#0288d1",
  outreach: "#fb8c00",
  submit: "#43a047",
  status_change: "#757575",
};

export default function CareGapTimeline({ history = [], loading }) {
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading history…
      </Typography>
    );
  }

  if (!history.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No events recorded yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {history.map((h, idx) => {
        const isSystem = h.employeeId === 0 || h.historyType === "status";
        const actionLabel = ACTION_LABEL[h.actionType] || h.actionType || "Event";
        const actionColor = ACTION_COLOR[h.actionType] || "#757575";

        return (
          <Box key={`${h.historyId}-${idx}`} sx={{ position: "relative", pl: 3.5 }}>
            <Box
              sx={{
                position: "absolute",
                left: 4,
                top: 4,
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: actionColor,
              }}
            />
            {idx < history.length - 1 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 8,
                  top: 18,
                  bottom: -8,
                  width: 2,
                  bgcolor: "divider",
                }}
              />
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip
                size="small"
                label={actionLabel}
                sx={{ bgcolor: actionColor, color: "#fff", fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                {fmtDateTime(h.eventDate)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              {isSystem ? <MemoryIcon fontSize="small" color="action" /> : <PersonOutlineIcon fontSize="small" color="action" />}
              <Typography variant="caption" color="text.secondary">
                {h.employeeFullName || "—"}
              </Typography>
            </Stack>

            {(h.workflowStatusFrom || h.workflowStatusTo) && (
              <Typography variant="body2">
                Workflow: <strong>{h.workflowStatusFrom || "—"}</strong> → <strong>{h.workflowStatusTo || "—"}</strong>
              </Typography>
            )}

            {(h.statusFrom || h.statusTo) && (
              <Typography variant="body2">
                Hability status: <strong>{h.statusFrom || "—"}</strong> → <strong>{h.statusTo || "—"}</strong>
              </Typography>
            )}

            {h.outreachChannel && (
              <Typography variant="body2">
                Channel: <strong>{h.outreachChannel}</strong>
                {h.outreachResult ? ` · Result: ${h.outreachResult}` : ""}
                {h.outreachNextAction ? ` · Next: ${h.outreachNextAction}` : ""}
              </Typography>
            )}

            {h.scheduledDate && (
              <Typography variant="body2">
                Scheduled: <strong>{fmtDateTime(h.scheduledDate)}</strong>
                {h.locationScheduled ? ` at ${h.locationScheduled}` : ""}
              </Typography>
            )}

            {h.submitTitle && (
              <Typography variant="body2">
                Availity submit: <strong>{h.submitTitle}</strong>
              </Typography>
            )}

            {h.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
                "{h.note}"
              </Typography>
            )}

            {idx < history.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        );
      })}
    </Stack>
  );
}
